<!-- @format -->

<template>
	<div>
		<slot :params="checkValue" v-bind="$attrs" @click="onShowDialog">
			<el-select
				v-model="checkIds"
				multiple
				:teleported="false"
				class="selectDeptClass"
				popper-class="selectDeptPopper"
				placeholder="请选择部门"
				style="width: 100%"
				v-bind="$attrs"
				@click="onShowDialog"
				@remove-tag="(e) => onDelete(e)"
			>
				<el-option v-for="item in checkValue" :key="item.deptId" :label="item.deptName" :value="item.deptId" />
			</el-select>
		</slot>
		<pc-dialog
			v-model="visible"
			append-to-body
			:close-on-click-modal="false"
			title="选择部门"
			width="800px"
			top="5vh"
			v-bind="dialogProps"
			@on-cancel="onCancel"
			@on-confirm="onConfirm"
		>
			<slot name="detail" :params="checkValue"></slot>
			<el-form :model="queryParams" :inline="true" @submit.prevent>
				<el-form-item label="部门名称" prop="deptName">
					<el-input
						v-model="queryParams.deptName"
						placeholder="请输入部门名称"
						clearable
						style="width: 240px"
						@keyup.enter="onSearch"
					/>
				</el-form-item>
			</el-form>

			<pc-table
				ref="tableRef"
				v-loading="loading"
				:data="deptList"
				max-height="360px"
				row-key="deptId"
				class="content"
				name="departPicker"
				:tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
				:show-total="false"
				:default-expand-all="true"
				@select="handleSelectionChange"
			>
				<el-table-column type="selection" width="55" />
				<el-table-column
					header-align="center"
					prop="deptName"
					label="部门名称"
					width="160"
					:show-overflow-tooltip="true"
				/>
				<el-table-column align="center" prop="orderNum" label="排序" />
				<el-table-column align="center" prop="deptCode" label="部门编码" />
				<el-table-column align="center" prop="deptDesc" label="部门说明" :show-overflow-tooltip="true" />
				<el-table-column align="center" label="创建时间" prop="createTime" width="160">
					<template #default="scope">
						<span>{{ parseTime(scope.row.createTime) }}</span>
					</template>
				</el-table-column>
			</pc-table>
		</pc-dialog>
	</div>
</template>

<script setup lang="ts">
	import { useRequest } from '@power/runtime-vue3';
	import { parseTime, handleTree } from '@/utils';
	import { PcDialog, PcTable } from '@/components';
	import { getDeptList } from '@/api/user';

	interface Props {
		modelValue: (string | number)[];
		dialogProps?: Record<string, any>;
		multiple?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {});

	const emit = defineEmits(['update:modelValue', 'onConfirm', 'onSelect']);

	const attrs = useAttrs();

	const tableRef = ref<InstanceType<typeof PcTable>>();

	const visible = ref<boolean>(false);

	const deptList = ref<
		{
			deptId: string;
			[key: string]: any;
		}[]
	>([]);

	const queryParams = ref({
		deptName: '',
	});

	// 选中的ids
	const checkIds = computed({
		get() {
			return props.modelValue || [];
		},
		set(val) {
			emit('update:modelValue', val);
		},
	});

	// 选中的带有label、value的数组
	const checkValue = ref<any[]>([]);

	// 选中的id缓存，点确定时才将数据给checkIds
	const cacheCheckValue = ref<any[]>([]);

	const onShowDialog = () => {
		if (!attrs.disabled) {
			visible.value = true;
			cacheCheckValue.value = JSON.parse(JSON.stringify(checkValue.value));
			getList();
		}
	};

	const { loading, load: getListLoad } = useRequest(getDeptList);

	const getList = () => {
		getListLoad({ ...queryParams.value }).then((res) => {
			deptList.value = handleTree(res, 'deptId');

			nextTick(() => {
				setRowSelection(
					res,
					cacheCheckValue.value.map((i) => i.deptId),
				);
			});
		});
	};

	const setRowSelection = (list, checkIds) => {
		list.forEach((row) => {
			if (checkIds.includes(row.deptId)) {
				tableRef.value?.ref?.toggleRowSelection(row, true);
			}
		});
	};

	const onDelete = (e) => {
		checkIds.value = checkIds.value.filter((i) => i !== e);
		checkValue.value = checkValue.value.filter((i) => i.deptId !== e);
	};

	const onSearch = () => {
		getList();
	};

	const onConfirm = (callback) => {
		visible.value = false;
		const deptIds = cacheCheckValue.value.map((i) => i.deptId);
		checkIds.value = deptIds;
		checkValue.value = toRaw(cacheCheckValue.value);
		emit('onConfirm', deptIds, cacheCheckValue);
		callback();
	};

	const onCancel = (callback) => {
		visible.value = false;
		callback();
	};

	// 多选框选中数据,现在是单选框
	function handleSelectionChange(selection, row) {
		if (props.multiple) {
			const index = cacheCheckValue.value.findIndex((i) => i.deptId === row.deptId);
			index !== -1 ? cacheCheckValue.value.splice(index, 1) : cacheCheckValue.value.push(row);
		} else {
			tableRef.value?.ref?.clearSelection();
			tableRef.value?.ref?.toggleRowSelection(selection.pop(), true);
			cacheCheckValue.value = [row];
		}
		emit('onSelect', cacheCheckValue);
	}

	onMounted(() => {
		// 获取对应label展示用，暂时用调接口方式
		checkIds.value.length &&
			getListLoad().then((res) => {
				const list = res.filter((i) => checkIds.value.includes(i.deptId));
				checkValue.value = list;
			});
	});

	defineExpose({ tableRef: tableRef.value?.ref, checkRows: checkValue });
</script>

<style lang="scss" scoped>
	.content {
		border: 1px solid var(--el-border-color);
		padding: 15px;
		border-radius: 5px;

		:deep(.el-table-column--selection.is-leaf .el-checkbox) {
			display: none;
		}
	}

	.selectDeptClass {
		:deep(.selectDeptPopper) {
			visibility: hidden;
		}
	}
</style>
